import {
  TableCell,
  TableRow,
  IconButton,
  Tooltip,
  Box,
  useTheme as useMuiTheme,
  alpha,
  Paper,
  TableContainer,
  Table,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useRef, useState, useEffect } from 'react';
import ItemTable from '../items/ItemTable';
import React from 'react';
import { CollapsibleRowProps, FolderNode } from '../../../types/FolderTypes';
import {
  buildFolderTree,
  deleteFolderHelper,
  createFolderHelper,
  mapFoldersToFolderNodes,
  isCreatingInFolder,
  useHandleMoveCurrentFolder,
} from './helpers';
import AssemblyTable from '../assemblies/AssemblyTable';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../../../stores/AssemblyStore';
import { useGlobalItemStore } from '../../../stores/globalItemStore';
import { useFolderStore } from '../../../stores/folderStore';
import { UnfoldLess, CreateNewFolder, Edit, Delete } from '@mui/icons-material';
import { ConfirmationDialog } from '../../common/ConfirmationDialog';
import { useTheme } from '../../../contexts/ThemeContext';
import { AnyEvent } from './folderTypes';
import FolderItem from './FolderItem';
import { useFolderMutation } from './hookHandler';
import { useFetchUser } from '../../../hooks/useFetchUsers';
import { UB_AccountUser } from '../../../api/protosCompiled/ub_users/ub_users_pb';

// Main component
const FolderNodes = ({ context, folderType, folderData }: CollapsibleRowProps) => {
  const muiTheme = useMuiTheme();
  const { darkMode } = useTheme();

  const isMoveMode = useStore(useFolderStore).isMoveMode;
  const setIsMoveMode = useStore(useFolderStore).setIsMoveMode;
  const currentFolder = useStore(useFolderStore).currentFolder;
  const setCurrentFolder = useStore(useFolderStore).setCurrentFolder;
  const openRows = useStore(useFolderStore).openRows;
  const toggleFolderExclusive = useStore(useFolderStore).toggleFolderExclusive;
  const editingFolderId = useStore(useFolderStore).editingFolderId;
  const setEditingFolderId = useStore(useFolderStore).setEditingFolderId;
  const closeAllFolders = useStore(useFolderStore).closeAllFolders;

  const isCreatingAssembly = useStore(useAssemblyStore).isCreatingAssembly;
  const setIsCreatingAssembly = useStore(useAssemblyStore).setIsCreatingAssembly;
  const setIsCreatingItem = useStore(useGlobalItemStore).setIsCreatingItem;
  const isViewingItemForm = useStore(useGlobalItemStore).isViewingItemForm;
  const setIsViewingItemForm = useStore(useGlobalItemStore).setIsViewingItemForm;
  const isEditMode = useStore(useAssemblyStore).isEditMode;

  // Local state
  const [tempFolderName, setTempFolderName] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<FolderNode | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<FolderNode | null>(null);
  const [folderModalMode, setFolderModalMode] = useState<'create' | 'rename'>('create');
  const [folderName, setFolderName] = useState('');
  const [folderNameError, setFolderNameError] = useState('');
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  //react query
  const { data: currentUser } = useFetchUser();

  // Mutations
  const { createMutation, updateMutation, deleteMutation } = useFolderMutation(folderType);

  // Hooks
  const moveCurrentFolder = useHandleMoveCurrentFolder();

  // Build folder tree
  const folderTree = React.useMemo(
    () =>
      folderData?.foldersList?.length
        ? buildFolderTree(folderType, mapFoldersToFolderNodes(folderData.foldersList))
        : [],
    [folderData?.foldersList, folderType]
  );

  // Add hasTableDescendants function here
  const hasTableDescendants = React.useCallback((folder: FolderNode): boolean => {
    // Check if this folder has any assembly or item files
    const hasFiles = folder.children.some(
      child => child.isfile && (child.entitytype === 'assembly' || child.entitytype === 'item')
    );

    if (hasFiles) return true;

    // Recursively check children
    return folder.children.some(child => hasTableDescendants(child));
  }, []);

  // Helper function to check if a folder is a descendant of another
  const isDescendantOf = (potentialChild: FolderNode, potentialParent: FolderNode): boolean => {
    // Check immediate parent
    if (potentialChild.parentid === potentialParent.folderid) {
      return true;
    }

    // Recursively check through all children of the potential parent
    for (const child of potentialParent.children) {
      if (isDescendantOf(potentialChild, child)) {
        return true;
      }
    }

    return false;
  };

  // Function to check if a folder is a viable move target
  const isViableMoveTarget = (folder: FolderNode): boolean => {
    if (!isMoveMode || !currentFolder) return true;

    return !(
      // Can't move to self
      (
        folder.folderid === currentFolder.folderid ||
        // Can't move to current parent (no-op)
        folder.folderid === currentFolder.parentid ||
        // Can't move to level 5+ folders
        folder.level >= 5 ||
        // Can't move to own descendants
        isDescendantOf(folder, currentFolder)
      )
    );
  };

  // Function to get tooltip text for move button
  const getMoveButtonTooltip = (folder: FolderNode): string => {
    if (!isMoveMode) return 'Move folder to another location';

    return isViableMoveTarget(folder)
      ? 'Set as destination folder'
      : 'Cannot move to this location';
  };

  const handleEnteryMode = (e: AnyEvent, folder: FolderNode) => {
    e.stopPropagation();
    e.preventDefault();
    if (folder.entitytype === 'assembly') {
      setIsCreatingAssembly(true);
      setIsCreatingItem(false);
      setIsViewingItemForm(false);
    } else if (folder.entitytype === 'item') {
      setIsCreatingAssembly(false);
      setIsCreatingItem(true);
      setIsViewingItemForm(true);
    }
  };

  // Event handlers - using AnyEvent to avoid TypeScript errors
  const handleSetMoveMode = (e: AnyEvent, folder: FolderNode) => {
    e.stopPropagation();

    // If already in move mode
    if (isMoveMode) {
      // If this is a viable target and not the folder being moved, use it as destination
      if (
        isViableMoveTarget(folder) &&
        currentFolder &&
        folder.folderid !== currentFolder.folderid
      ) {
        moveCurrentFolder({
          e: e as any,
          isMoveMode,
          setIsMoveMode,
          destinationFolder: folder,
          folderBeingMoved: currentFolder,
        });
      }
      // Otherwise do nothing - this prevents starting a new move operation while one is in progress
      return;
    }

    // Only enter move mode if not already in move mode
    setIsMoveMode(true);
    setCurrentFolder(folder); // Set currentFolder for both UI selection and as the folder being moved
  };

  const handleCreateFolder = (e: AnyEvent, folder: FolderNode) => {
    e.stopPropagation();
    createFolderHelper(e as any, folder, createMutation);
  };

  const handleEditStart = (folder: FolderNode) => {
    setEditingFolderId(folder.folderid);
    setTempFolderName(folder.name);

    // Use setTimeout to ensure the DOM has updated before trying to focus
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        // Position cursor at the end of the text
        inputRef.current.setSelectionRange(folder.name.length, folder.name.length);
      }
    }, 50);
  };

  const handleEditCancel = () => {
    setEditingFolderId(null);
    setTempFolderName('');
  };

  const handleEditChange = (e: AnyEvent) => {
    const position = e.target.selectionStart;
    setTempFolderName(e.target.value);
    setCursorPosition(position);
  };

  const handleRenameFolder = (e: AnyEvent, folder: FolderNode) => {
    e.stopPropagation();
    e.preventDefault();

    // Don't rename if name is empty
    if (!tempFolderName.trim()) {
      setEditingFolderId(null);
      return;
    }

    const updatedFolder = {
      ...folder,
      name: tempFolderName,
    };

    updateMutation(updatedFolder);

    setEditingFolderId(null);
  };

  const handleDeleteClick = (e: AnyEvent, folder: FolderNode) => {
    e.stopPropagation();
    setFolderToDelete(folder);
    setShowDeleteConfirmation(true);
    setSelectedFolder(folder);
  };

  const handleDeleteConfirm = () => {
    if (folderToDelete) {
      deleteFolderHelper(folderToDelete, deleteMutation);
      setShowDeleteConfirmation(false);
      setFolderToDelete(null);
      setSelectedFolder(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setFolderToDelete(null);
    setSelectedFolder(null);
  };

  // Update cursor position
  useEffect(() => {
    if (inputRef.current && cursorPosition !== null) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [tempFolderName, cursorPosition]);

  // Handle clicks outside the input field to complete editing
  useEffect(() => {
    if (editingFolderId) {
      const handleOutsideClick = (e: MouseEvent) => {
        // Skip if clicking on the input itself
        if (inputRef.current?.contains(e.target as Node)) {
          return;
        }

        // Find the folder being edited
        const findFolder = (folders: FolderNode[], id: string): FolderNode | null => {
          for (const folder of folders) {
            if (folder.folderid === id) return folder;
            if (folder.children.length > 0) {
              const found = findFolder(folder.children, id);
              if (found) return found;
            }
          }
          return null;
        };

        const folder = findFolder(folderTree, editingFolderId);
        if (folder) {
          // Only save if the name has changed and is not empty
          if (tempFolderName.trim() && tempFolderName !== folder.name) {
            const updatedFolder = {
              ...folder,
              name: tempFolderName,
            };

            updateMutation(updatedFolder);
          }
        }

        // Always exit edit mode
        setEditingFolderId(null);
      };

      // Add the event listener
      document.addEventListener('mousedown', handleOutsideClick);

      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingFolderId, tempFolderName, folderTree, updateMutation]);

  // Update the handleToggleFolder function to prevent reassigning the folder being moved
  const handleToggleFolder = (folderId: string) => {
    // Toggle the folder open/closed
    toggleFolderExclusive(folderId);

    // If we're in move mode, don't change which folder is selected for moving
    if (isMoveMode) {
      return; // Exit early - don't change currentFolder when in move mode
    }

    // Find the folder that's being toggled
    const findFolder = (folders: FolderNode[], id: string): FolderNode | null => {
      for (const folder of folders) {
        if (folder.folderid === id) return folder;
        if (folder.children.length > 0) {
          const found = findFolder(folder.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const folder = findFolder(folderTree, folderId);
    if (!folder) return;

    // Only update selection if the folder is being opened and not in edit mode
    const isOpening = !openRows[folderId];
    if (isOpening && !isEditMode) {
      if (isCreatingAssembly && folder.entitytype === 'assembly') {
        setCurrentFolder(folder);
      } else if (!isCreatingAssembly && folder.entitytype === 'item') {
        setCurrentFolder(folder);
      }
    }
  };

  // Update the handleCancelMoveMode function
  const handleCancelMoveMode = (e: AnyEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsMoveMode(false);
    // Don't clear currentFolder as it might be used for other purposes
  };

  // Add useEffect to ensure focus when editing starts
  useEffect(() => {
    if (editingFolderId && inputRef.current) {
      inputRef.current.focus();
      if (tempFolderName) {
        inputRef.current.setSelectionRange(tempFolderName.length, tempFolderName.length);
      }
    }
  }, [editingFolderId, tempFolderName]);

  // Render folder tree recursively
  const renderFolderTree = (folders: FolderNode[], depth = 0) => {
    return folders.map(folder => {
      // Get regular folders vs files
      const normalFolders = folder.children.filter((child: FolderNode) => !child.isfile);
      const assemblyFiles = folder.children.filter(
        (child: FolderNode) => child.isfile && child.entitytype === 'assembly'
      );
      const itemFiles = folder.children.filter(
        (child: FolderNode) => child.isfile && child.entitytype === 'item'
      );

      // Render all child folders first
      const nestedFolders = renderFolderTree(normalFolders, depth + 1);

      // Render assembly files
      const assemblyItems = assemblyFiles.map((assemblyFolder: FolderNode) => (
        <TableRow
          key={`assembly-${assemblyFolder.folderid}`}
          sx={{
            width: '100%',
            backgroundColor: 'transparent !important',
            '&:hover': {
              backgroundColor: 'transparent !important',
            },
          }}
        >
          <TableCell
            colSpan={2}
            sx={{
              p: 0,
              width: '100%',
              paddingLeft: `${depth * 4 + 4}px`,
              backgroundColor: 'transparent !important',
              border: 'none',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: `${depth * 4}px`,
                top: 0,
                bottom: 0,
                width: '3px',
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.primary.light, 0.5)
                  : alpha(muiTheme.palette.primary.main, 0.5),
                borderRadius: '0 2px 2px 0',
              },
            }}
          >
            <AssemblyTable context={context} folder={assemblyFolder} />
          </TableCell>
        </TableRow>
      ));

      // Render item files
      const itemItems = itemFiles.map((item: FolderNode, index: number) => (
        <TableRow
          key={`item-${item.folderid || index}`}
          sx={{
            width: '100%',
            backgroundColor: 'transparent !important',
            '&:hover': {
              backgroundColor: 'transparent !important',
            },
          }}
        >
          <TableCell
            colSpan={2}
            sx={{
              p: 0,
              width: '100%',
              paddingLeft: `${depth * 4 + 4}px`,
              backgroundColor: 'transparent !important',
              border: 'none',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: `${depth * 4}px`,
                top: 0,
                bottom: 0,
                width: '3px',
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.primary.light, 0.5)
                  : alpha(muiTheme.palette.primary.main, 0.5),
                borderRadius: '0 2px 2px 0',
              },
            }}
          >
            <ItemTable context={context} folder={item} />
          </TableCell>
        </TableRow>
      ));

      // Combine all nested items
      const nestedItems = [...nestedFolders, ...assemblyItems, ...itemItems];

      // Check if this folder is selected
      const isSelected = !isMoveMode && isCreatingInFolder(folder, isViewingItemForm);

      // Determine if this is a viable move target before rendering
      const isViableTarget = isViableMoveTarget(folder);
      const moveTooltip = getMoveButtonTooltip(folder);

      // Render the folder with its children
      return (
        <FolderItem
          key={folder.folderid}
          folder={folder}
          depth={depth}
          nestedItems={nestedItems}
          openRows={openRows}
          toggleFolder={handleToggleFolder}
          isSelected={isSelected}
          theme={muiTheme}
          editingFolderId={editingFolderId}
          tempFolderName={tempFolderName}
          inputRef={inputRef}
          context={context}
          isMoveMode={isMoveMode}
          currentFolder={currentFolder}
          onEditChange={handleEditChange}
          onEditFinish={handleRenameFolder}
          onEditCancel={handleEditCancel}
          onCreateFolder={handleCreateFolder}
          onSetMoveMode={handleSetMoveMode}
          onEditStart={handleEditStart}
          onDeleteClick={handleDeleteClick}
          onCancelMoveMode={handleCancelMoveMode}
          isViableTarget={isViableTarget}
          moveTooltip={moveTooltip}
          hasTableDescendants={hasTableDescendants(folder)}
          folderType={folderType}
          onEnteryMode={handleEnteryMode}
          currentUser={currentUser || ({} as UB_AccountUser.AsObject)}
        />
      );
    });
  };

  const handleFolderModalClose = () => {
    setShowFolderModal(false);
    setFolderName('');
    setFolderNameError('');
  };

  const handleFolderSubmit = () => {
    if (folderModalMode === 'create') {
      handleCreateFolder(null, {
        ...selectedFolder,
        name: folderName,
      } as FolderNode);
    } else {
      handleRenameFolder(null, {
        ...selectedFolder,
        name: folderName,
      } as FolderNode);
    }
    handleFolderModalClose();
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleNewFolderClick = () => {
    setFolderModalMode('create');
    setShowFolderModal(true);
  };

  const handleRenameClick = () => {
    setFolderModalMode('rename');
    setShowFolderModal(true);
  };

  return (
    <Box
      sx={{
        backgroundColor: darkMode
          ? alpha(muiTheme.palette.background.default, 0.9)
          : muiTheme.palette.background.default,
        color: muiTheme.palette.text.primary,
        borderRadius: 1,
        height: '100%',
        overflow: 'auto',
        scrollBehavior: 'smooth',
        overscrollBehavior: 'contain',
        '&::-webkit-scrollbar': {
          width: '8px',
          backgroundColor: darkMode
            ? alpha(muiTheme.palette.background.paper, 0.05)
            : alpha(muiTheme.palette.background.paper, 0.1),
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '4px',
          backgroundColor: darkMode
            ? alpha(muiTheme.palette.primary.light, 0.2)
            : alpha(muiTheme.palette.primary.main, 0.2),
          '&:hover': {
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.primary.light, 0.4)
              : alpha(muiTheme.palette.primary.main, 0.3),
          },
        },
        '@media (pointer: fine)': {
          scrollbarWidth: 'thin',
          msOverflowStyle: 'auto',
        },
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Header with collapse button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1,
          borderBottom: `1px solid ${alpha(muiTheme.palette.divider, 0.1)}`,
        }}
      >
        <Tooltip title="Close all folders" arrow>
          <IconButton
            onClick={closeAllFolders}
            size="small"
            sx={{
              color: muiTheme.palette.text.secondary,
              '&:hover': {
                color: muiTheme.palette.primary.main,
                backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
              },
            }}
            aria-label="Close all folders"
          >
            <UnfoldLess fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Folder tree */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          width: '100%',
          bgcolor: 'transparent',
          backgroundColor: muiTheme.palette.background.default,
        }}
      >
        <Table
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
          }}
        >
          <TableBody>{renderFolderTree(folderTree)}</TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={showDeleteConfirmation}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${selectedFolder?.isfile ? 'File' : 'Folder'}`}
        message={
          selectedFolder?.isfile
            ? `Are you sure you want to delete ${selectedFolder?.name || 'this item'}?`
            : `Are you sure you want to delete ${selectedFolder?.name || 'this item'}? Warning: This will delete all contents within this folder.`
        }
      />

      {/* Folder Create/Rename Modal */}
      <Dialog
        open={showFolderModal}
        onClose={handleFolderModalClose}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.paper, 0.9)
              : muiTheme.palette.background.paper,
            boxShadow: darkMode ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.2)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: darkMode ? muiTheme.palette.text.primary : muiTheme.palette.text.primary,
            pb: 1,
          }}
        >
          {folderModalMode === 'create' ? 'Create New Folder' : 'Rename Folder'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={folderName}
            onChange={e => setFolderName(e.target.value)}
            error={!!folderNameError}
            helperText={folderNameError}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.background.default, 0.6)
                  : 'transparent',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: muiTheme.palette.primary.main,
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: darkMode ? muiTheme.palette.primary.light : muiTheme.palette.primary.main,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleFolderModalClose}
            sx={{
              color: darkMode
                ? alpha(muiTheme.palette.text.primary, 0.7)
                : muiTheme.palette.text.secondary,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFolderSubmit}
            variant="contained"
            disabled={!folderName.trim() || !!folderNameError}
          >
            {folderModalMode === 'create' ? 'Create' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
        }
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: darkMode
              ? alpha(muiTheme.palette.background.paper, 0.9)
              : muiTheme.palette.background.paper,
            boxShadow: darkMode ? '0 2px 10px rgba(0, 0, 0, 0.5)' : '0 2px 10px rgba(0, 0, 0, 0.2)',
            borderRadius: 1,
          },
        }}
      >
        {!selectedFolder?.isfile && (
          <MenuItem
            onClick={() => handleNewFolderClick()}
            sx={{
              color: darkMode ? muiTheme.palette.text.primary : muiTheme.palette.text.primary,
              '&:hover': {
                backgroundColor: darkMode
                  ? alpha(muiTheme.palette.primary.main, 0.15)
                  : alpha(muiTheme.palette.primary.main, 0.08),
              },
            }}
          >
            <ListItemIcon>
              <CreateNewFolder fontSize="small" color={darkMode ? 'primary' : 'primary'} />
            </ListItemIcon>
            <ListItemText>New Folder</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleRenameClick()}
          sx={{
            color: darkMode ? muiTheme.palette.text.primary : muiTheme.palette.text.primary,
            '&:hover': {
              backgroundColor: darkMode
                ? alpha(muiTheme.palette.primary.main, 0.15)
                : alpha(muiTheme.palette.primary.main, 0.08),
            },
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" color={darkMode ? 'info' : 'info'} />
          </ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => selectedFolder && handleDeleteClick(null, selectedFolder)}
          sx={{
            color: darkMode ? muiTheme.palette.error.light : muiTheme.palette.error.main,
            '&:hover': {
              backgroundColor: darkMode
                ? alpha(muiTheme.palette.error.main, 0.15)
                : alpha(muiTheme.palette.error.main, 0.08),
            },
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default React.memo(FolderNodes);
